/**
 * ResourcesTransactionsTable Component
 *
 * Transaction table for Recursos Dashboard displaying financial transactions.
 * Adapted from DashboardPaymentsTable with specific features for recursos management.
 *
 * Features:
 * - Search by tracking ID, responsible name, or transaction code
 * - Filter by status, date range, and value range
 * - Pagination (10 rows per page)
 * - Copy transaction code to clipboard
 * - 6 columns: Tracking ID, Responsável, Data, Valor, Código da Transação, Status
 *
 * Design specs from Figma:
 * - Background: #15203C (dark blue card)
 * - Header row: #0C1427
 * - 10 rows per page
 * - Exact color matching for status badges
 */

import { useState, useMemo, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import {
  TransactionFilter
} from "@/types/recursos-dashboard.interface";
import StatusBadge from "@/components/dashboard/status-badge";
import Pagination from "@/components/dashboard/pagination";
import FilterDialog from "@/components/dashboard/filter-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Copy, Filter } from "lucide-react";
import { Transaction } from "@/interfaces/dashboard.interface";

interface ResourcesTransactionsTableProps {
  /** Array of transaction data */
  transactions: Transaction[];

  /** Loading state */
  isLoading?: boolean;
}

/**
 * Formats number as Brazilian Real currency
 * Example: 5000.00 → "R$ 5.000,00"
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export function ResourcesTransactionsTable({
  transactions,
  isLoading = false,
}: ResourcesTransactionsTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<TransactionFilter>({
    status: [],
    dateRange: undefined,
    valueRange: { min: 0, max: Infinity },
    searchQuery: "",
  });

  const pageSize = 10; // 10 rows per page as per Figma

  // Search filter
  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return transactions;

    return transactions.filter(
      (t) =>
        t.trackingId.toLowerCase().includes(term) ||
        t.responsible.name.toLowerCase().includes(term) ||
        t.transactionCode.toLowerCase().includes(term)
    );
  }, [transactions, searchTerm]);

  // Advanced filters (status, date, value)
  const filteredByFilters = useMemo(() => {
    let result = filtered;

    // Status filter
    if (filters.status && filters.status.length > 0) {
      result = result.filter((t) => filters.status!.includes(t.status));
    }

    // Date range filter
    if (filters.dateRange?.start) {
      result = result.filter((t) => {
        const [day, month, year] = t.date.split("/");
        const transactionDate = new Date(`${year}-${month}-${day}`);
        const [startDay, startMonth, startYear] = filters.dateRange!.start.split("/");
        const filterDate = new Date(`${startYear}-${startMonth}-${startDay}`);
        return transactionDate >= filterDate;
      });
    }

    if (filters.dateRange?.end) {
      result = result.filter((t) => {
        const [day, month, year] = t.date.split("/");
        const transactionDate = new Date(`${year}-${month}-${day}`);
        const [endDay, endMonth, endYear] = filters.dateRange!.end.split("/");
        const filterDate = new Date(`${endYear}-${endMonth}-${endDay}`);
        return transactionDate <= filterDate;
      });
    }

    // Value range filter
    if (filters.valueRange) {
      result = result.filter((t) => {
        return t.value >= filters.valueRange!.min && t.value <= filters.valueRange!.max;
      });
    }

    return result;
  }, [filtered, filters]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredByFilters.length / pageSize));

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredByFilters.slice(start, start + pageSize);
  }, [filteredByFilters, currentPage, pageSize]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredByFilters.length]);

  // Copy transaction code to clipboard
  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast({
        title: "Copiado!",
        description: "Código copiado para a área de transferência",
      });
    } catch {
      toast({
        title: "Erro",
        description: "Não foi possível copiar",
        variant: "destructive",
      });
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setFilters({
      status: [],
      dateRange: undefined,
      valueRange: { min: 0, max: Infinity },
      searchQuery: "",
    });
  };

  return (
    <div className="bg-[#15203C] rounded-[20px] p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <h2 className="text-[18px] font-bold text-white">
          Transações de Recursos
        </h2>

        <div className="flex items-center gap-3">
          {/* Search */}
          <Input
            type="text"
            placeholder="Procurar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-[#0C1427] border border-[#5D5D5D] text-white placeholder:text-gray-400 w-[240px]"
            end={<Search size={18} />}
          />

          {/* Filter Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setFilterOpen(true)}
            className="border-[#5D5D5D] hover:bg-white/5 text-white"
          >
            <Filter className="size-4 mr-2" />
            Filtros
          </Button>
        </div>
      </div>

      {/* Table Header */}
      <div className="bg-[#0C1427] rounded-lg px-5 py-[10px] mb-1 hidden md:grid grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr] gap-4">
        <p className="text-[12px] font-bold text-[#F6F6F6]">Tracking ID</p>
        <p className="text-[12px] font-bold text-[#F6F6F6]">Responsável</p>
        <p className="text-[12px] font-bold text-[#F6F6F6]">Data</p>
        <p className="text-[12px] font-bold text-[#F6F6F6]">Valor</p>
        <p className="text-[12px] font-bold text-[#F6F6F6]">Código da Transação</p>
        <p className="text-[12px] font-bold text-[#F6F6F6]">Status</p>
      </div>

      {/* Table Rows */}
      <div className="space-y-0">
        {isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-[62px]" />
            ))}
          </div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-sm">Nenhuma transação encontrada</p>
            {(searchTerm || (filters.status && filters.status.length > 0)) && (
              <button
                onClick={clearFilters}
                className="text-[#7846FF] text-sm mt-2 hover:underline"
              >
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          paginated.map((transaction, index) => (
            <div key={transaction.id}>
              <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr] gap-4 items-center">
                {/* Tracking ID */}
                <p className="text-[14px] text-[#F6F6F6]">
                  <span className="md:hidden font-bold">Tracking ID: </span>
                  {transaction.trackingId}
                </p>

                {/* Responsible */}
                <div className="flex items-center gap-[10px]">
                  <div className="size-[22px] rounded-full bg-gray-600 overflow-hidden flex-shrink-0">
                    {transaction.responsible.avatar ? (
                      <img
                        src={transaction.responsible.avatar}
                        alt={transaction.responsible.name}
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="size-full bg-gray-600" />
                    )}
                  </div>
                  <p className="text-[14px] font-semibold text-[#F6F6F6]">
                    {transaction.responsible.name}
                  </p>
                </div>

                {/* Date */}
                <p className="text-[14px] text-[#F6F6F6]">
                  <span className="md:hidden font-bold">Data: </span>
                  {transaction.date}
                </p>

                {/* Value */}
                <p className="text-[14px] font-semibold text-[#F6F6F6]">
                  {formatCurrency(transaction.value)}
                </p>

                {/* Transaction Code */}
                <div className="flex items-center gap-[10px]">
                  <p className="text-[12px] text-[#F6F6F6] truncate">
                    {transaction.transactionCode.substring(0, 27)}...
                  </p>
                  <button
                    onClick={() => handleCopyCode(transaction.transactionCode)}
                    className="flex-shrink-0 hover:opacity-70 transition-opacity"
                    aria-label="Copiar código"
                  >
                    <Copy className="size-[18px] text-[#F6F6F6]" />
                  </button>
                </div>

                {/* Status */}
                <div>
                  <StatusBadge status={transaction.status} />
                </div>
              </div>

              {/* Row Divider */}
              {index < paginated.length - 1 && (
                <div className="border-b border-gray-800/60" />
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer - Pagination */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6">
        <p className="text-[14px] text-[#888888]">
          Exibindo {paginated.length} de {filteredByFilters.length} transações
        </p>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Filter Dialog */}
      <FilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={{
          status: filters.status || [],
          dateFrom: filters.dateRange?.start || "",
          dateTo: filters.dateRange?.end || "",
          valueMin: filters.valueRange?.min || 0,
          valueMax: filters.valueRange?.max === Infinity ? 0 : filters.valueRange?.max || 0,
        }}
        onApply={(newFilters) => {
          setFilters({
            status: newFilters.status,
            dateRange: {
              start: newFilters.dateFrom,
              end: newFilters.dateTo,
            },
            valueRange: {
              min: newFilters.valueMin,
              max: newFilters.valueMax === 0 ? Infinity : newFilters.valueMax,
            },
            searchQuery: searchTerm,
          });
          setFilterOpen(false);
        }}
      />
    </div>
  );
}
